import { FormEvent, lazy, ReactElement, Suspense, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, DEFAULT_DIAL_CODE, getFlagEmoji } from '~/utils/constants/countries';
import { mapboxAccessToken } from '~/utils/constants/env';
import { useFilePreview } from '~/utils/hooks/useFilePreview';
import { FormValues, IFormField } from '~/utils/interface/setup';

// mapbox-gl is large; it only loads when a form actually has a map field.
const LocationMapField = lazy(() => import('./LocationMapField'));

type Props = {
  title: string;
  fields: IFormField[];
  initial: FormValues;
  isExisting?: boolean;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
};

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground';

const ImagePreview = ({ value }: { value: FormValues[string] }) => {
  const src = useFilePreview(value);
  if (!src) return null;

  return <img src={src} alt='' className='h-16 w-16 rounded-full border border-border object-cover' />;
};

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const FormModal = ({ title, fields, initial, isExisting, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<FormValues>(initial);
  const [showErrors, setShowErrors] = useState(false);
  const [pickedCountry, setPickedCountry] = useState<string>();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const visibleFields = fields.filter(
    (field) => !(isExisting && field.hideOnEdit) && (!field.showWhen || field.showWhen(values))
  );

  const isBlank = (value: FormValues[string]) => value === '' || value === null || value === undefined;

  // a map field is "filled" once a pin (lat + lng) has been placed
  const isMissing = (field: IFormField) => {
    if (!field.required) return false;
    if (field.type === 'map') return isBlank(values.lat) || isBlank(values.lng);
    if (field.type === 'phone') return !String(values[field.name] ?? '').trim();
    return !String(values[field.name] ?? '').trim();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (visibleFields.some(isMissing)) {
      setShowErrors(true);
      return;
    }
    // the phone field shows the default dial code even when none is stored, so submit what is shown
    const withDialCodes = { ...values };
    visibleFields
      .filter((field) => field.type === 'phone')
      .forEach((field) => {
        const key = field.countryName ?? 'countryCode';
        if (!withDialCodes[key]) withDialCodes[key] = DEFAULT_DIAL_CODE;
      });
    onSubmit(withDialCodes);
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'
      // only a press that starts on the dimmed backdrop closes: dragging a text selection out of the form doesn't
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <form
        onSubmit={handleSubmit}
        role='dialog'
        aria-modal='true'
        aria-label={title}
        className='flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xl'
      >
        <div className='flex shrink-0 items-center justify-between border-b border-border px-5 py-3'>
          <h2 className='text-base font-semibold text-foreground'>{title}</h2>
          <button
            type='button'
            onClick={onCancel}
            aria-label={t('pages.setup.close')}
            className='inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
          >
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
              <path d='M18 6 6 18M6 6l12 12' />
            </svg>
          </button>
        </div>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5'>

        {visibleFields.map((field) => {
          const node = ((): ReactElement | null => {
          const label = field.label ?? t(field.labelKey);
          const value = values[field.name];
          const error = showErrors && isMissing(field);

          if (field.type === 'checkboxGroup') {
            const all = (field.options ?? []).map((option) => option.value);
            const selected = String(value ?? '').split(',').filter(Boolean);
            const set = (next: string[]) =>
              setValues((prev) => ({ ...prev, [field.name]: all.filter((v) => next.includes(v)).join(',') }));
            const allSelected = all.length > 0 && all.every((v) => selected.includes(v));
            return (
              <fieldset key={field.name} className='flex flex-col gap-2 text-sm'>
                <legend>
                  {label}
                  {field.required && ' *'}
                </legend>
                <label className='flex items-center gap-2 font-medium'>
                  <input type='checkbox' checked={allSelected} onChange={() => set(allSelected ? [] : all)} />
                  <span>{t('pages.setup.selectAll')}</span>
                </label>
                <div className={`flex flex-col gap-2 rounded-md border p-3 ${error ? 'border-destructive' : 'border-border'}`}>
                  {field.options?.map((option) => (
                    <label key={option.value} className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        checked={selected.includes(option.value)}
                        onChange={(e) =>
                          set(e.target.checked ? [...selected, option.value] : selected.filter((v) => v !== option.value))
                        }
                      />
                      <span>{option.label ?? t(option.labelKey ?? '')}</span>
                    </label>
                  ))}
                </div>
                {error && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
              </fieldset>
            );
          }

          if (field.type === 'switch') {
            const on = value === true;
            const disabled = field.disabledWhen?.(values) ?? false;
            return (
              <div key={field.name} className={`flex items-center justify-between gap-3 text-sm ${disabled ? 'opacity-50' : ''}`}>
                <span id={`switch-${field.name}`}>{label}</span>
                <button
                  type='button'
                  role='switch'
                  aria-checked={on}
                  aria-labelledby={`switch-${field.name}`}
                  disabled={disabled}
                  onClick={() => setValues((prev) => ({ ...prev, [field.name]: !on }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                    on ? 'bg-primary' : 'bg-border'
                  }`}
                >
                  <span
                    aria-hidden='true'
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                      on ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            );
          }

          if (field.type === 'checkbox') {
            const disabled = field.disabledWhen?.(values) ?? false;
            return (
              <label
                key={field.name}
                className={`flex items-center gap-2 text-sm ${field.indent ? 'ml-6' : ''} ${disabled ? 'opacity-50' : ''}`}
              >
                <input
                  type='checkbox'
                  checked={value === true}
                  disabled={disabled}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.checked }))}
                />
                <span>{label}</span>
              </label>
            );
          }

          const locked = !!isExisting && !!field.lockedOnEdit;

          if (field.type === 'map') {
            // without a (public) Mapbox token the lat/lng inputs below are the only way to place a zone
            if (!mapboxAccessToken) return null;
            return (
              <div key={field.name} className='flex flex-col gap-1'>
                <span className='text-sm'>
                  {label}
                  {field.required && ' *'}
                </span>
                <Suspense fallback={<p className='text-sm text-muted-foreground'>{t('common.loading')}</p>}>
                  <LocationMapField
                    values={values}
                    onChange={(partial) => setValues((prev) => ({ ...prev, ...partial }))}
                  />
                </Suspense>
                {error && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
              </div>
            );
          }

          if (field.type === 'heading') {
            return (
              <span
                key={field.name}
                className={`text-sm ${field.indent ? 'ml-6' : ''} ${field.disabledWhen?.(values) ? 'opacity-50' : ''}`}
              >
                {label}
              </span>
            );
          }

          if (field.type === 'phone') {
            const countryKey = field.countryName ?? 'countryCode';
            const dialCode = typeof values[countryKey] === 'string' && values[countryKey] ? String(values[countryKey]) : DEFAULT_DIAL_CODE;
            // several countries share a dial code (+1): keep the picked one for the flag
            const country =
              COUNTRIES.find((c) => c.code === pickedCountry && c.dialCode === dialCode) ??
              COUNTRIES.find((c) => c.dialCode === dialCode);
            return (
              <div key={field.name} className='flex flex-col gap-1 text-sm'>
                <span>
                  {label}
                  {field.required && ' *'}
                </span>
                <div
                  className={`flex items-stretch overflow-hidden rounded-md border bg-background ${
                    error ? 'border-destructive' : 'border-border'
                  }`}
                >
                  <div className='relative flex shrink-0 items-center gap-1.5 whitespace-nowrap border-r border-border px-3'>
                    <span aria-hidden='true'>
                      {country ? getFlagEmoji(country.code) : ''} {dialCode}
                    </span>
                    <span aria-hidden='true' className='text-[0.6rem] leading-none text-muted-foreground'>
                      &#9660;
                    </span>
                    <select
                      aria-label={t('pages.setup.fields.countryCode')}
                      value={country?.code ?? ''}
                      onChange={(e) => {
                        const next = COUNTRIES.find((c) => c.code === e.target.value);
                        if (!next) return;
                        setPickedCountry(next.code);
                        setValues((prev) => ({ ...prev, [countryKey]: next.dialCode }));
                      }}
                      className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {getFlagEmoji(c.code)} {c.name} ({c.dialCode})
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type='tel'
                    inputMode='tel'
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    className='w-full bg-transparent px-3 py-2 text-sm text-foreground outline-none'
                  />
                </div>
                {error && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
              </div>
            );
          }

          if (field.type === 'range') {
            const current = Number(value ?? field.min ?? 0);
            return (
              <label key={field.name} className='flex flex-col gap-1 text-sm'>
                <span>
                  {label}: {current}
                </span>
                <input
                  type='range'
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={current}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: Number(e.target.value) }))}
                />
              </label>
            );
          }

          if (field.type === 'weekdays') {
            // stored as "1,3,5"; day n sits at index n - weekStart of Mon..Sun order
            const start = field.weekStart ?? 1;
            const selected = String(value ?? '').split(',').filter(Boolean);
            const toggle = (day: string) => {
              const next = selected.includes(day) ? selected.filter((d) => d !== day) : [...selected, day];
              setValues((prev) => ({ ...prev, [field.name]: next.sort().join(',') }));
            };
            const days = WEEKDAY_KEYS.map((key, index) => ({
              key,
              // Mon..Sat -> index+1; Sun -> 7 (weekStart 1) or 0 (weekStart 0)
              day: String(index === 6 ? (start === 1 ? 7 : 0) : index + 1),
            }));
            return (
              <div key={field.name} className='flex flex-col gap-2 text-sm'>
                <span>
                  {label}
                  {field.required && ' *'}
                </span>
                <div className='flex flex-wrap gap-3'>
                  {days.map(({ key, day }) => (
                    <label key={key} className='flex items-center gap-1'>
                      <input type='checkbox' checked={selected.includes(day)} onChange={() => toggle(day)} />
                      <span>{t(`pages.setup.weekdays.${key}`)}</span>
                    </label>
                  ))}
                </div>
                {error && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
              </div>
            );
          }

          if (field.type === 'select') {
            return (
              <label key={field.name} className='flex flex-col gap-1 text-sm'>
                <span>
                  {label}
                  {field.required && ' *'}
                </span>
                <select
                  className={`${inputClass} ${error ? 'border-destructive' : ''}`}
                  value={String(value ?? '')}
                  disabled={locked}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                >
                  <option value=''>{t(field.emptyLabelKey ?? 'pages.setup.select')}</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label ?? t(option.labelKey ?? '')}
                    </option>
                  ))}
                </select>
                {field.emptyHint && !field.options?.length && (
                  <span className='text-xs text-muted-foreground'>
                    {t(field.emptyHint.messageKey)}{' '}
                    <Link to={field.emptyHint.to} className='font-medium text-foreground underline'>
                      {t(field.emptyHint.linkKey)}
                    </Link>
                  </span>
                )}
                {error && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
              </label>
            );
          }

          if (field.type === 'file') {
            const current = typeof value === 'string' && value ? value : undefined;
            return (
              <div key={field.name} className='flex flex-col gap-2 text-sm'>
                <span>{label}</span>
                {value instanceof File ? (
                  <span className='text-xs text-muted-foreground'>{value.name}</span>
                ) : (
                  current && (
                    <a href={current} target='_blank' rel='noreferrer' className='text-xs underline'>
                      {t('pages.setup.currentFile')}
                    </a>
                  )
                )}
                <input
                  type='file'
                  accept={field.accept}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setValues((prev) => ({ ...prev, [field.name]: file }));
                  }}
                />
              </div>
            );
          }

          if (field.type === 'image') {
            return (
              <div key={field.name} className='flex flex-col gap-2 text-sm'>
                <span>{label}</span>
                <ImagePreview value={value} />
                <input
                  type='file'
                  accept='image/*'
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setValues((prev) => ({ ...prev, [field.name]: file }));
                  }}
                />
              </div>
            );
          }

          const common = {
            value: typeof value === 'string' || typeof value === 'number' ? value : '',
            disabled: locked,
            className: `${inputClass} ${error ? 'border-destructive' : ''}`,
            onChange: (e: { target: { value: string } }) =>
              setValues((prev) => ({ ...prev, [field.name]: e.target.value })),
          };

          return (
            <label key={field.name} className='flex flex-col gap-1 text-sm'>
              <span>
                {label}
                {field.required && ' *'}
              </span>
              {field.type === 'textarea' ? (
                <textarea rows={3} {...common} />
              ) : (
                <input type={field.type} {...common} />
              )}
              {error && <span className='text-xs text-destructive'>{t('pages.setup.required')}</span>}
            </label>
          );
          })();

          // fields with their own indent handling (checkbox / heading / switch) skip the generic wrapper
          const selfIndented = field.type === 'checkbox' || field.type === 'heading' || field.type === 'switch';
          return field.indent && !selfIndented ? (
            <div key={field.name} className='ml-6'>
              {node}
            </div>
          ) : (
            node
          );
        })}

        </div>

        <div className='flex shrink-0 justify-end gap-2 border-t border-border px-5 py-3'>
          <button
            type='button'
            onClick={onCancel}
            className='rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted'
          >
            {t('pages.setup.cancel')}
          </button>
          <button
            type='submit'
            className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90'
          >
            {t('pages.setup.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormModal;
